import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { RootState } from '../../../app/store/store';
import { checkUsernameAvailable, registerCompany, registerEmployee } from './thunks';
import { CONSENT_VERSION } from '@shared/ui/legal-consent/legalDocuments';
import { FetchStatusType } from '../../../types/interfaces';

interface UserData {
  email: string;
  username: string;
  password: string;
  first_name: string;
  last_name: string;
  mobile_phone: string;
  terms: boolean;
  consent_marketing?: boolean;
}

interface Address {
  [key: string]: string | 'legal' | 'physical';
  country: string;
  region: string;
  city: string;
  street: string;
  house: string;
  apartment: string;
  postcode: string;
  type: 'legal' | 'physical';
}

interface LegalEntity {
  name: string;
  email: string;
  phones: string[];
  inn: string;
  online_documents: boolean;
  addresses: Address[];
  category_guid: string;
  partner_guid: string;
}

interface Contact {
  name: string;
  surname: string;
  patronymic: string;
  position: string;
  personal_phone: string;
}

interface RegistrationState {
  invite: string;
  legal_entities: LegalEntity[];
  contacts: Contact[];
  name: string;
  user: UserData;
  terms: boolean;
  status: FetchStatusType;
  error: string;
  currentStep: number;
  manager: Manager;
  guid: string;
  useExistingAccount: boolean;
  existingUserToken: string;
}

interface Manager {
  name: string;
  email: string;
  guid: string;
}

const initialState: RegistrationState = {
  status: 'idle',
  error: '',
  invite: '',
  guid: '',
  manager: {
    name: '',
    email: '',
    guid: '',
  },
  user: {
    username: '',
    password: '',
    email: '',
    first_name: '',
    last_name: '',
    mobile_phone: '',
    terms: false,
  },
  legal_entities: [{
    name: '',
    inn: '',
    email: '',
    phones: [],
    online_documents: true,
    addresses: [
      {
        country: '',
        region: '',
        city: '',
        street: '',
        house: '',
        apartment: '',
        postcode: '',
        type: 'legal',
      },
      {
        country: '',
        region: '',
        city: '',
        street: '',
        house: '',
        apartment: '',
        postcode: '',
        type: 'physical',
      },
    ],
    category_guid: '',
    partner_guid: '',
  }],
  contacts: [
    {
      name: '',
      surname: '',
      patronymic: '',
      position: '',
      personal_phone: '',
    }
  ],
  name: '',
  terms: true,
  currentStep: 0,
  useExistingAccount: false,
  existingUserToken: '',
};

const registrationSlice = createSlice({
  name: 'registration',
  initialState,
  reducers: {
    resetRegistration: () => initialState,
    addCompanyPartialData(state, action: PayloadAction<{ name: string; inn: string; email: string; online_documents: boolean; phones: string[]; }>) {
      const { name, inn, email, phones, online_documents } = action.payload;
      state.name = name;
      state.legal_entities[0].name = name;
      state.legal_entities[0].inn = inn;
      state.legal_entities[0].email = email;
      state.legal_entities[0].phones = phones;
      state.legal_entities[0].online_documents = online_documents;
      state.currentStep = 2;
    },
    addCompanyData(state, action: PayloadAction<any>) {
      if (action.payload.type === 'employee') {
        // Для employee-инвайта компания не передаётся (юзер
        // присоединяется к существующей), поэтому всё, что
        // нужно вытащить из partial_data — это имя и фамилию,
        // которые задал автор инвайта в форме «Новое приглашение».
        // Раньше тут стоял голый return, и поля «Имя/Фамилия» на
        // первом шаге регистрации оставались пустыми — это и был
        // баг «при регистрации по приглашению не подставляется
        // имя».
        const employeePartial = (action.payload.partial_data || {}) as {
          first_name?: string;
          last_name?: string;
        };
        if (employeePartial.first_name) {
          state.user.first_name = employeePartial.first_name;
        }
        if (employeePartial.last_name) {
          state.user.last_name = employeePartial.last_name;
        }
        // invite-id всё равно нужен на сабмите (registerEmployee
        // тащит его из state.invite — см. RegistrationStepCredentials).
        state.invite = action.payload.id || '';
        return;
      }
      // Для company-инвайта partial_data может прилететь null'ом
      // (например, если 1С-экспорт ещё не отработал). В этом
      // случае ничего не подставляем — иначе деструктуризация
      // упадёт с TypeError.
      if (!action.payload.partial_data) {
        state.invite = action.payload.id || '';
        return;
      }
      const { inn, name, email, phone, manager, guid, legalAddressJSON, actualAddressJSON, categoryGuid, codeAlfa3, countryCode } = action.payload.partial_data;

      // Страна из инвайта приходит на верхнем уровне (codeAlfa3='KAZ'/'RUS'
      // или countryCode='398'/'643'), а не внутри адресов: legalAddressJSON
      // и actualAddressJSON у партнёров из 1С часто пустые. Чтобы шаги
      // регистрации (лейбл «ИНН» vs «БИН/ИИН», подсказки в адресах)
      // могли отличить КЗ от РФ — выводим страну отсюда и подставляем
      // в `address.country` ('kz'|'ru'|'').
      const inferCountry = (): 'kz' | 'ru' | '' => {
        const a3 = (codeAlfa3 || '').toString().toUpperCase();
        if (a3 === 'KAZ') return 'kz';
        if (a3 === 'RUS') return 'ru';
        const cc = (countryCode || '').toString();
        if (cc === '398') return 'kz';
        if (cc === '643') return 'ru';
        return '';
      };
      const inviteCountry = inferCountry();

      const safeParseJSON = (jsonString: string) => {
        try {
          if (!jsonString) return null;
          let json = JSON.parse(jsonString);
          return json;
        } catch (err) {
          console.error("Ошибка при парсинге JSON:", err);
          return null;
        }
      };

      const parseAddress = (addressObj: any, type: 'legal' | 'physical'): Address | null => {
        if (!addressObj) return null;
        return {
          // Если в JSON-адресе страны нет — фолбэк на страну из
          // верхнеуровневого codeAlfa3/countryCode инвайта.
          country: addressObj.country || inviteCountry || '',
          region: addressObj.area || '',
          city: addressObj.city || '',
          street: addressObj.street || '',
          house: addressObj.houseNumber || '',
          apartment: addressObj.apartments?.[0]?.number || '',
          postcode: addressObj.ZIPcode || '',
          type: type,
        };
      };

      const legalAddressObj = safeParseJSON(legalAddressJSON);
      const actualAddressObj = safeParseJSON(actualAddressJSON);

      if (!legalAddressObj || !actualAddressObj) {
        console.log("Один из адресов некорректен и не может быть обработан.");
      }

      // Дефолтные пустые адреса, если JSON в инвайте отсутствует или
      // не распарсился. Страну всё равно проставляем из верхнеуровневых
      // codeAlfa3/countryCode — иначе шаги регистрации не отличат КЗ от РФ
      // (см. лейбл налогового номера, валидацию индекса и т.д.).
      const defaultLegalAddress: Address = {
        country: inviteCountry || '',
        region: '',
        city: '',
        street: '',
        house: '',
        apartment: '',
        postcode: '',
        type: 'legal',
      };

      const defaultPhysicalAddress: Address = {
        country: inviteCountry || '',
        region: '',
        city: '',
        street: '',
        house: '',
        apartment: '',
        postcode: '',
        type: 'physical',
      };
      
      const legalAddressParsed = parseAddress(legalAddressObj, 'legal') || defaultLegalAddress;
      const actualAddressParsed = parseAddress(actualAddressObj, 'physical') || defaultPhysicalAddress;
      const legalEntity: LegalEntity = {
        name: name || '',
        email: email || '',
        phones: [phone].filter(Boolean),
        inn: inn || '',
        online_documents: true,
        addresses: [legalAddressParsed, actualAddressParsed],
        category_guid: categoryGuid || '',
        partner_guid: guid || '',
      };

      state.name = name || '';
      state.invite = action.payload.id || '';
      state.manager = manager || '';
      state.legal_entities[0] = (legalEntity);
    },
    addLegalAddress(state, action: PayloadAction<Address>) {
      const legalAddress = action.payload;
      state.legal_entities[0].addresses = state.legal_entities[0].addresses.map(address => {
        if (address.type === 'legal') {
          return {...legalAddress, type: 'legal'};
        }
        return address;
      });
      state.currentStep = 3;
    },
    addDeliveryAddress(state, action: PayloadAction<Address>) {
      const deliveryAddress = action.payload;
      state.legal_entities[0].addresses = state.legal_entities[0].addresses.map(address => {
        if (address.type === 'physical') {
          return {...deliveryAddress, type: 'physical'};
        }
        return address;
      });
      state.currentStep = 4;
    },
    addContactPerson(state, action: PayloadAction<Contact>) {
      const contactPerson = action.payload;
      state.contacts[0] = contactPerson;
      state.currentStep = 5;
    },
    confirmRegistration(state) {
      // Placeholder
    },
    setStep(state, action: PayloadAction<number>) {
      state.currentStep = action.payload;
    },
    previousStep(state) {
      if (state.currentStep > 0) {
        state.currentStep--;
      }
    },
    setExistingUser(state, action: PayloadAction<{ token: string; user: UserData }>) {
      state.useExistingAccount = true;
      state.existingUserToken = action.payload.token;
      state.user = action.payload.user;
      state.currentStep = 1;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(checkUsernameAvailable.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(checkUsernameAvailable.fulfilled, (state, action) => {
        state.status = 'succeeded';
        const { username, password, email, first_name, last_name, mobile_phone } = action.payload;
        state.user.username = username;
        state.user.password = password;
        state.user.email = email;
        state.user.first_name = first_name;
        state.user.last_name = last_name;
        state.currentStep = 1;
        state.user.mobile_phone = mobile_phone;
        // Отметки согласий из шага 0 — уйдут в /companies/register/ (legal).
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const consents = action.payload as any;
        state.user.terms = Boolean(consents.terms);
        state.user.consent_marketing = Boolean(consents.consent_marketing);
      })
      .addCase(checkUsernameAvailable.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.error.message || 'Failed to check username';
      })
      .addCase(registerCompany.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(registerCompany.fulfilled, (state, action) => {
        state.status = 'succeeded';
        state.currentStep = 6;
      })
      .addCase(registerCompany.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.error.message || 'Failed to register company';
      })
      .addCase(registerEmployee.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(registerEmployee.fulfilled, (state, action) => {
        state.status = 'succeeded';
        state.currentStep = 6;
      })
      .addCase(registerEmployee.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.error.message || 'Failed to register employee';
      })
      // (registerGuest / useGuestInvite addCases удалены вместе
      // с концепцией гостя — редьюсер ничего о них больше не знает.)
      ;
  },
});

export const {
  addCompanyPartialData,
  resetRegistration,
  addCompanyData,
  addLegalAddress,
  addDeliveryAddress,
  addContactPerson,
  confirmRegistration,
  previousStep,
  setStep,
  setExistingUser,
} = registrationSlice.actions;

export const selectCredentials = (state: RootState) => state.registration.user;

export const selectLegalEntities = (state: RootState) => state.registration.legal_entities;

export const selectLegalEntityLegalAddress = (state: RootState) => {
  const legalEntity = state.registration.legal_entities[0];
  return legalEntity.addresses.find(address => address.type === 'legal');
};

export const selectLegalEntitiesPhysicalAddress = (state: RootState) => {
  const legalEntity = state.registration.legal_entities[0];
  return legalEntity.addresses.find(address => address.type === 'physical');
};

export const selectRegistrationData = (state: RootState) => {
  const contacts = state.registration.contacts.map((contact) => ({
    name: contact.name,
    surname: contact.surname,
    patronymic: contact.patronymic,
    position: contact.position,
    personal_phone: contact.personal_phone,
  }));

  const legal_entities = state.registration.legal_entities.map((entity) => ({
    name: entity.name,
    email: entity.email,
    inn: entity.inn,
    phones: entity.phones,
    category_guid: entity.category_guid,
    partner_guid: entity.partner_guid,
    online_documents: entity.online_documents,
    addresses: entity.addresses.map((address) => ({
      country: address.country,
      region: address.region,
      city: address.city,
      street: address.street,
      house: address.house,
      apartment: address.apartment,
      postcode: address.postcode,
      type: address.type,
    })),
  }));

  const user = state.registration.useExistingAccount 
    ? undefined
    : {
        username: state.registration.user.username,
        password: state.registration.user.password,
        email: state.registration.user.email,
        first_name: state.registration.user.first_name,
        last_name: state.registration.user.last_name,
        mobile_phone: state.registration.user.mobile_phone,
        invite: state.registration.invite,
      };

  return {
    invite: state.registration.invite,
    name: state.registration.name,
    guid: state.registration.guid,
    terms: state.registration.terms,
    // Согласия фиксирует бэк (legal). Вход существующим аккаунтом форму с
    // чекбоксами не проходит — согласие у него спросит окно при входе в ЛК.
    consent_personal_data: state.registration.useExistingAccount
      ? false
      : Boolean(state.registration.user.terms),
    consent_marketing: state.registration.useExistingAccount
      ? false
      : Boolean(state.registration.user.consent_marketing),
    consent_version: CONSENT_VERSION,
    user,
    legal_entities,
    contacts,
    useExistingAccount: state.registration.useExistingAccount,
    token: state.registration.useExistingAccount ? state.registration.existingUserToken : undefined,
  };
};

export const selectContacts = (state: RootState) => state.registration.contacts;
export const selectCurrentStep = (state: RootState) => state.registration.currentStep;
export const selectResponsibleManager = (state: RootState) => state.registration.manager;
export const selectRegistrationStatus = (state: RootState) => state.registration.status;
export const selectUseExistingAccount = (state: RootState) => state.registration.useExistingAccount;

export default registrationSlice.reducer;

